// ===========================================
// TIPOS: Módulo de Vencimientos Fiscales
// ===========================================
// Tipos derivados de las vistas/tablas generadas en database.ts
// + tipos custom para el módulo de calendario/vencimientos.

import type { Database } from '@/types/database'

// ===========================================
// Tipos derivados de Database (vistas y tablas)
// ===========================================

/** Fila de la vista vista_proximos_vencimientos — vencimiento con datos del cliente */
export type VencimientoConClienteBase = Database['public']['Views']['vista_proximos_vencimientos']['Row']

/** Tipo extendido con campos de lazy materialization (presentes en la vista actualizada) */
export type VencimientoConCliente = VencimientoConClienteBase & {
  es_virtual?: boolean | null
  calendario_afip_id?: string | null
  origen_tipo?: string | null
}

/** Fila de la tabla vencimientos_fiscales */
export type VencimientoFiscal = Database['public']['Tables']['vencimientos_fiscales']['Row']

/** Fila de la vista vista_tareas_completas */
export type TareaCompleta = Database['public']['Views']['vista_tareas_completas']['Row']

// ===========================================
// Tipos custom (no generados por Supabase)
// ===========================================

/** Estados posibles del ciclo de vida de un vencimiento fiscal */
export type EstadoVencimientoFiscal = 'pendiente' | 'presentado' | 'pagado' | 'vencido' | 'plan_pago'

export const ESTADOS_VENCIMIENTO_FISCAL: Record<EstadoVencimientoFiscal, { label: string; color: string; bgColor: string }> = {
  pendiente: { label: 'Pendiente', color: 'text-yellow-700', bgColor: 'bg-yellow-100' },
  presentado: { label: 'Presentado', color: 'text-green-700', bgColor: 'bg-green-100' },
  pagado: { label: 'Pagado', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  vencido: { label: 'Vencido', color: 'text-red-700', bgColor: 'bg-red-100' },
  plan_pago: { label: 'Plan de Pago', color: 'text-violet-700', bgColor: 'bg-violet-100' },
}

/** Datos para registrar pago de vencimiento */
export interface RegistrarPagoData {
  monto_pagado?: number | null
  fecha_pago: string
  forma_pago?: string | null
  referencia_pago?: string | null
  observaciones?: string | null
}

/** Datos para registrar VEP */
export interface RegistrarVEPData {
  vep_numero: string
  vep_monto?: number | null
  vep_fecha_vencimiento?: string | null
}

/** Datos para marcar plan de pago */
export interface MarcarPlanPagoData {
  cuotas?: number | null
  referencia?: string | null
  observaciones?: string | null
}

/** Estadísticas de vencimientos para dashboard cards */
export interface EstadisticasVencimientos {
  /** Pendientes con fecha < hoy (requieren atención) */
  vencidos: number
  /** Pendientes con fecha entre hoy y hoy+7 */
  vencenEstaSemana: number
  /** Pendientes con fecha entre hoy y fin de mes actual */
  vencenEsteMes: number
  /** Presentados con fecha_presentacion en el mes en curso */
  presentadosEsteMes: number
  /** Pagados con fecha_pago en el mes en curso */
  pagadosEsteMes: number
  // Mantenidos por compatibilidad con consumidores existentes
  /** @deprecated usar vencenEstaSemana */
  proximaSemana: number
  /** @deprecated usar vencenEsteMes */
  proximoMes: number
  pendientes: number
  presentados: number
  pagados: number
  enPlanPago: number
}

/** Agrupación de vencimientos por día (para CalendarioMensual) */
export interface VencimientosDelDia {
  fecha: string
  vencimientos: VencimientoConCliente[]
  totalPendientes: number
  totalPresentados: number
  totalVencidos: number
  totalPagados: number
}

/** Resultado de generación de vencimientos (fiscales o recurrentes) */
export interface ResultadoGeneracionVencimientos {
  vencimientos_creados: number
  clientes_procesados: number
  vencimientos_omitidos: number
}

/**
 * Tabla: impuestos_cliente
 * Relación many-to-many entre clientes y catalogo_impuestos
 */
export interface ImpuestosClienteRow {
  id: string
  cliente_id: string
  impuesto_id: string
  numero_inscripcion: string | null
  fecha_inscripcion: string | null
  fecha_baja: string | null
  alicuota: number | null
  regimen: string | null
  activo: boolean
  observaciones: string | null
  created_at: string
  updated_at: string
}

export interface ImpuestosClienteInsert {
  id?: string
  cliente_id: string
  impuesto_id: string
  numero_inscripcion?: string | null
  fecha_inscripcion?: string | null
  fecha_baja?: string | null
  alicuota?: number | null
  regimen?: string | null
  activo?: boolean
  observaciones?: string | null
  created_at?: string
  updated_at?: string
}

export interface ImpuestosClienteUpdate {
  id?: string
  cliente_id?: string
  impuesto_id?: string
  numero_inscripcion?: string | null
  fecha_inscripcion?: string | null
  fecha_baja?: string | null
  alicuota?: number | null
  regimen?: string | null
  activo?: boolean
  observaciones?: string | null
  updated_at?: string
}

/**
 * Tipo con join al catálogo de impuestos
 * Usado en queries con select de relaciones
 */
export interface ImpuestoClienteConCatalogo {
  id: string
  cliente_id: string
  impuesto_id: string
  numero_inscripcion: string | null
  fecha_inscripcion: string | null
  fecha_baja: string | null
  alicuota: number | null
  regimen: string | null
  activo: boolean
  observaciones: string | null
  created_at: string
  updated_at: string
  impuesto: {
    id: string
    codigo: string
    nombre: string
    nombre_corto: string | null
    categoria: string
    color: string | null
  }
}

// VencimientoConCliente ahora se exporta arriba, derivado de la vista generada

/**
 * Grupos temporales para clasificación de vencimientos
 */
export type GrupoTemporal =
  | 'vencidos'
  | 'hoy'
  | 'mañana'
  | 'estaSemana'
  | 'esteMes'
  | 'futuro'

export const GRUPOS_TEMPORALES: Record<GrupoTemporal, { label: string; orden: number }> = {
  vencidos: { label: 'Vencidos', orden: 0 },
  hoy: { label: 'Hoy', orden: 1 },
  mañana: { label: 'Mañana', orden: 2 },
  estaSemana: { label: 'Esta semana', orden: 3 },
  esteMes: { label: 'Este mes', orden: 4 },
  futuro: { label: 'Posteriores', orden: 5 },
}

// ===========================================
// TIPOS: Prórrogas Fiscales
// ===========================================

export interface Prorroga {
  id: string
  impuesto_id: string
  periodo: string
  terminacion_cuit: string | null
  cliente_id: string | null
  organizacion_id: string | null
  fecha_original: string
  fecha_nueva: string
  motivo: string
  fuente: string | null
  referencia: string | null
  activo: boolean
  created_by: string | null
  created_at: string
  updated_at: string
}

/**
 * Fila del calendario AFIP ofrecida como opción al registrar una prórroga (ORR-277).
 *
 * El período NO se tipea: sale de acá, que es exactamente el registro contra el que
 * después matchea la prórroga (`pf.periodo = vac.periodo`, comparación de texto exacto).
 */
export interface VencimientoCalendarioOpcion {
  id: string
  periodo: string
  fecha_vencimiento: string
  terminacion_cuit: string | null
}

export interface ProrrogaInsert {
  impuesto_id: string
  periodo: string
  terminacion_cuit?: string | null
  cliente_id?: string | null
  organizacion_id?: string | null
  fecha_original: string
  fecha_nueva: string
  motivo: string
  fuente?: string | null
  referencia?: string | null
  activo?: boolean
  created_by?: string | null
}

// ===========================================
// TIPOS: Datos de materialización
// ===========================================

/** Datos extra para materializar un vencimiento virtual al tomar una acción */
export interface DatosMaterializacion {
  calendario_afip_id: string
  cliente_id: string
}

// ===========================================
// TIPOS: Alertas programadas (ORR-232)
// ===========================================

/** `solo_proximo` se apaga tras avisar; `todos_los_siguientes` es una regla permanente */
export type AlcanceAlerta = 'solo_proximo' | 'todos_los_siguientes'

export interface AlertaVencimiento {
  id: string
  organizacion_id: string
  usuario_id: string
  cliente_id: string
  alcance: AlcanceAlerta
  calendario_afip_id: string | null
  vencimiento_fiscal_id: string | null
  impuesto_id: string | null
  dias_antes: number
  activo: boolean
  created_at: string
}

/**
 * Ancla de la alerta. Los vencimientos virtuales no tienen id estable (la vista
 * lo regenera en cada lectura), así que se identifican por calendario_afip_id;
 * los recurrentes, que no vienen del calendario AFIP, por su id físico.
 */
export interface CrearAlertaData {
  cliente_id: string
  alcance: AlcanceAlerta
  dias_antes: number
  calendario_afip_id?: string | null
  vencimiento_fiscal_id?: string | null
  impuesto_id?: string | null
}
